import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faPlug,
  faBell,
  faShieldAlt,
  faDatabase,
  faPlus,
  faEdit,
  faTrash,
  faToggleOn,
  faToggleOff,
  faSync,
  faCheckCircle,
  faExclamationCircle,
  faTimesCircle,
  faQuestionCircle,
  faSpinner,
  faSave,
  faChevronRight,
  faPaintBrush,
  faGlobe,
  faUpload,
  faImage,
  faEnvelope,
  faDownload,
  faHistory,
  faServer,
  faKey,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import siemService from '../services/siemService';
import notificationService from '../services/notificationService';
import remediationService from '../services/remediationService';
import settingsService from '../services/settingsService';
import exposiqService from '../services/exposiqService';
import tenantService from '../services/tenantService';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';

const Settings = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { t, language, setLanguage, languages, availableLanguages } = useLanguage();
  const [activeTab, setActiveTab] = useState('branding');
  const [loading, setLoading] = useState(false);
  const logoInputRef = useRef(null);
  const logoLightInputRef = useRef(null);

  // Integration keys state
  const [integrationKeys, setIntegrationKeys] = useState([]);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [keyForm, setKeyForm] = useState({ name: '', platform: 'exposiq', tenant: '' });
  const isSuperAdmin = user?.role === 'admin';
  const [tenants, setTenants] = useState([]);

  // Super admins can target any tenant when generating a key (else it defaults to
  // their own tenant, which for a superuser is the parent — e.g. Zerberos).
  useEffect(() => {
    if (!isSuperAdmin) return;
    tenantService.getTenants().then(list => setTenants(Array.isArray(list) ? list : (list?.tenants || []))).catch(() => {});
  }, [isSuperAdmin]);

  // SIEM state
  const [siemIntegrations, setSiemIntegrations] = useState([]);
  const [siemTypes, setSiemTypes] = useState([]);
  const [showSiemModal, setShowSiemModal] = useState(false);
  const [editingSiem, setEditingSiem] = useState(null);
  const [siemForm, setSiemForm] = useState({
    name: '',
    type: 'splunk',
    config: {},
    queryConfig: { queryEnabled: false },
    eventTypes: {
      actions: true,
      findings: true,
      agentEvents: true,
      playbookExecutions: true
    }
  });

  // Notification preferences state
  const [notifPreferences, setNotifPreferences] = useState(null);

  // Remediation state
  const [remediationStats, setRemediationStats] = useState(null);

  // Branding/App settings state
  const [appSettings, setAppSettings] = useState(null);
  const [brandingForm, setBrandingForm] = useState({
    appName: '',
    tagline: '',
    primaryColor: '#8B5CF6',
    secondaryColor: '#EF4444'
  });
  const [localizationForm, setLocalizationForm] = useState({
    defaultLanguage: 'en',
    availableLanguages: ['en', 'de', 'fr', 'it'],
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    timezone: 'Europe/Zurich'
  });

  // General settings (SMTP) state
  const [generalForm, setGeneralForm] = useState({
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: false,
    fromEmail: '',
    fromName: ''
  });

  // Backup state
  const [backupList, setBackupList] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupProgress, setBackupProgress] = useState(null);
  const restoreInputRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'integrations') {
      fetchSiemIntegrations();
      fetchSiemTypes();
    } else if (activeTab === 'notifications') {
      fetchNotificationPreferences();
    } else if (activeTab === 'remediation') {
      fetchRemediationStats();
    } else if (activeTab === 'branding' || activeTab === 'localization') {
      fetchAppSettings();
    } else if (activeTab === 'backup') {
      fetchBackupList();
    } else if (activeTab === 'apikeys') {
      fetchIntegrationKeys();
    }
  }, [activeTab]);

  const fetchIntegrationKeys = async () => {
    try {
      setLoading(true);
      const data = await exposiqService.getIntegrationKeys();
      setIntegrationKeys(data);
    } catch (error) {
      console.error('Error fetching integration keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    try {
      setLoading(true);
      const result = await exposiqService.generateIntegrationKey(keyForm);
      setNewKeyResult(result);
      setKeyForm({ name: '', platform: 'exposiq', tenant: '' });
      fetchIntegrationKeys();
    } catch (error) {
      toast.error('Failed to generate key');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (id) => {
    if (!window.confirm('Revoke this integration key? ExposIQ will no longer be able to send webhooks with it.')) return;
    try {
      await exposiqService.revokeIntegrationKey(id);
      toast.success('Key revoked');
      fetchIntegrationKeys();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  const fetchAppSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      setAppSettings(data);
      if (data.branding) {
        setBrandingForm({
          appName: data.branding.appName || '',
          tagline: data.branding.tagline || '',
          primaryColor: data.branding.primaryColor || '#8B5CF6',
          secondaryColor: data.branding.secondaryColor || '#EF4444'
        });
      }
      if (data.localization) {
        setLocalizationForm({
          defaultLanguage: data.localization.defaultLanguage || 'en',
          availableLanguages: data.localization.availableLanguages || ['en', 'de', 'fr', 'it'],
          dateFormat: data.localization.dateFormat || 'DD.MM.YYYY',
          timeFormat: data.localization.timeFormat || '24h',
          timezone: data.localization.timezone || 'Europe/Zurich'
        });
      }
    } catch (error) {
      console.error('Error fetching app settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSiemIntegrations = async () => {
    try {
      setLoading(true);
      const data = await siemService.getIntegrations();
      setSiemIntegrations(data);
    } catch (error) {
      console.error('Error fetching SIEM integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchSiemTypes = async () => {
    try {
      const data = await siemService.getSIEMTypes();
      setSiemTypes(data);
    } catch (error) {
      console.error('Error fetching SIEM types:', error);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const prefs = await notificationService.getPreferences();
      setNotifPreferences(prefs);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const fetchRemediationStats = async () => {
    try {
      const stats = await remediationService.getStats();
      setRemediationStats(stats);
    } catch (error) {
      console.error('Error fetching remediation stats:', error);
    }
  };

  const handleSaveSiem = async () => {
    try {
      setLoading(true);
      if (editingSiem) {
        await siemService.updateIntegration(editingSiem._id, siemForm);
        toast.success('Integration updated');
      } else {
        await siemService.createIntegration(siemForm);
        toast.success('Integration created');
      }
      setShowSiemModal(false);
      setEditingSiem(null);
      setSiemForm({
        name: '',
        type: 'splunk',
        config: {},
        queryConfig: { queryEnabled: false },
        eventTypes: { actions: true, findings: true, agentEvents: true, playbookExecutions: true }
      });
      fetchSiemIntegrations();
    } catch (error) {
      console.error('Error saving SIEM integration:', error);
      toast.error('Failed to save integration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSiem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;
    try {
      await siemService.deleteIntegration(id);
      toast.success('Integration deleted');
      fetchSiemIntegrations();
    } catch (error) {
      console.error('Error deleting integration:', error);
      toast.error('Failed to delete integration');
    }
  };

  const handleToggleSiem = async (id) => {
    try {
      const result = await siemService.toggleIntegration(id);
      toast.success(`Integration ${result.isEnabled ? 'enabled' : 'disabled'}`);
      fetchSiemIntegrations();
    } catch (error) {
      console.error('Error toggling integration:', error);
      toast.error('Failed to toggle integration');
    }
  };

  const handleTestSiem = async (id) => {
    try {
      setLoading(true);
      const result = await siemService.testIntegration(id);
      if (result.success) {
        toast.success('Test event sent successfully!');
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
      fetchSiemIntegrations();
    } catch (error) {
      console.error('Error testing integration:', error);
      toast.error('Test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifPreferences = async () => {
    try {
      await notificationService.updatePreferences(notifPreferences);
      toast.success('Preferences saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    }
  };

  const handleSeedRemediation = async () => {
    try {
      setLoading(true);
      const result = await remediationService.seedData();
      toast.success(`Seeded ${result.created} remediations`);
      fetchRemediationStats();
    } catch (error) {
      console.error('Error seeding remediation data:', error);
      toast.error('Failed to seed data');
    } finally {
      setLoading(false);
    }
  };

  // Backup functions
  const fetchBackupList = async () => {
    try {
      setBackupLoading(true);
      const response = await fetch('/api/backup/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBackupList(data.backups || []);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      setBackupProgress('Creating backup...');
      const response = await fetch('/api/backup/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Backup created: ${data.filename}`);
        fetchBackupList();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to create backup');
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setBackupLoading(false);
      setBackupProgress(null);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await fetch(`/api/backup/download/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Backup downloaded');
      } else {
        toast.error('Failed to download backup');
      }
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup');
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete backup "${filename}"?`)) {
      return;
    }
    try {
      const response = await fetch(`/api/backup/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        toast.success('Backup deleted');
        fetchBackupList();
      } else {
        toast.error('Failed to delete backup');
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!window.confirm('WARNING: This will overwrite all current data. Are you sure you want to restore from this backup?')) {
      event.target.value = '';
      return;
    }

    try {
      setBackupLoading(true);
      setBackupProgress('Restoring backup...');

      const formData = new FormData();
      formData.append('backup', file);

      const response = await fetch('/api/backup/restore', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Backup restored successfully! ${data.message || ''}`);
        // Reload to refresh all data
        setTimeout(() => window.location.reload(), 2000);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to restore backup');
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Failed to restore backup');
    } finally {
      setBackupLoading(false);
      setBackupProgress(null);
      event.target.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getHealthIcon = (status) => {
    const icons = {
      healthy: <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />,
      degraded: <FontAwesomeIcon icon={faExclamationCircle} className="text-yellow-500" />,
      unhealthy: <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />,
      unknown: <FontAwesomeIcon icon={faQuestionCircle} className="text-gray-500" />
    };
    return icons[status] || icons.unknown;
  };

  const tabs = [
    { id: 'branding', label: t('settings.branding') || 'Branding', icon: faPaintBrush },
    { id: 'localization', label: t('settings.localization') || 'Localization', icon: faGlobe },
    { id: 'apikeys', label: 'Integration Keys', icon: faKey },
    { id: 'integrations', label: t('settings.siemIntegrations') || 'SIEM Integrations', icon: faPlug },
    { id: 'notifications', label: t('settings.notifications') || 'Notifications', icon: faBell },
    { id: 'remediation', label: t('settings.remediation') || 'Remediation Database', icon: faShieldAlt },
    { id: 'backup', label: t('settings.backup') || 'Backup & Restore', icon: faDatabase },
    { id: 'general', label: t('settings.general') || 'General', icon: faCog }
  ];

  const renderIntegrationKeys = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Integration Keys</h2>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            API keys for external platforms (ExposIQ) to authenticate with PurpleHQ
          </p>
        </div>
        <button
          onClick={() => { setShowKeyModal(true); setNewKeyResult(null); }}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Generate Key
        </button>
      </div>

      {/* New key result banner */}
      {newKeyResult?.key && (
        <div className={`mb-4 p-4 rounded-lg border ${darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faKey} className="text-yellow-500" />
            <span className="font-semibold text-yellow-500">New Key Generated — Copy It Now!</span>
          </div>
          <p className={`text-sm mb-2 ${darkMode ? 'text-yellow-300/70' : 'text-yellow-700'}`}>
            This key will only be shown once. Copy it and configure it in ExposIQ.
          </p>
          <div className="flex items-center gap-2">
            <code className={`flex-1 p-2 rounded text-sm font-mono break-all ${darkMode ? 'bg-gray-800 text-green-400' : 'bg-white text-green-700 border border-gray-200'}`}>
              {newKeyResult.key}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(newKeyResult.key); toast.success('Key copied!'); }}
              className="px-3 py-2 rounded bg-teal-600 text-white hover:bg-teal-700 flex-shrink-0"
              title="Copy key"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
        </div>
      )}

      {loading && integrationKeys.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-teal-500" />
        </div>
      ) : integrationKeys.length === 0 ? (
        <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <FontAwesomeIcon icon={faKey} className="text-4xl text-gray-400 mb-4" />
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>No integration keys</p>
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Generate a key to allow ExposIQ to send scan results to PurpleHQ
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {integrationKeys.map((k) => (
            <div
              key={k._id}
              className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faKey} className={k.isActive && !k.revokedAt ? 'text-teal-500' : 'text-gray-500'} />
                  <div>
                    <h3 className="font-medium">{k.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {k.keyPrefix}...
                      </code>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        k.revokedAt ? 'bg-red-500/20 text-red-400'
                          : k.isActive ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {k.revokedAt ? 'Revoked' : k.isActive ? 'Active' : 'Disabled'}
                      </span>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {k.platform}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {k.usageCount || 0} uses
                    {k.lastUsedAt && ` · Last: ${new Date(k.lastUsedAt).toLocaleDateString()}`}
                  </span>
                  {!k.revokedAt && (
                    <button
                      onClick={() => handleRevokeKey(k._id)}
                      className="p-2 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                      title="Revoke key"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>
              {k.expiresAt && (
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Expires: {new Date(k.expiresAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Generate Key Modal */}
      {showKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <h2 className="text-xl font-semibold mb-4">Generate Integration Key</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Key Name</label>
                <input
                  type="text"
                  value={keyForm.name}
                  onChange={e => setKeyForm(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                  placeholder="ExposIQ Production"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Platform</label>
                <select
                  value={keyForm.platform}
                  onChange={e => setKeyForm(prev => ({ ...prev, platform: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="exposiq">ExposIQ</option>
                  <option value="generic">Generic</option>
                </select>
              </div>
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant</label>
                  <select
                    value={keyForm.tenant}
                    onChange={e => setKeyForm(prev => ({ ...prev, tenant: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                  >
                    <option value="">My tenant (default)</option>
                    {tenants.map(t => (
                      <option key={t._id} value={t._id}>{t.name}{t.slug ? ` (${t.slug})` : ''}</option>
                    ))}
                  </select>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    The key (and imported results) belong to this tenant. Pick the project's tenant, not your super-admin home tenant.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowKeyModal(false); setNewKeyResult(null); }}
                className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => { handleGenerateKey(); setShowKeyModal(false); }}
                disabled={!keyForm.name}
                className={`px-4 py-2 rounded text-white ${keyForm.name ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-500 cursor-not-allowed'}`}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderSiemIntegrations = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">SIEM Integrations</h2>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Configure integrations to send events to your SIEM
          </p>
        </div>
        <button
          onClick={() => {
            setEditingSiem(null);
            setSiemForm({
              name: '',
              type: 'splunk',
              config: {},
              eventTypes: { actions: true, findings: true, agentEvents: true, playbookExecutions: true }
            });
            setShowSiemModal(true);
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Integration
        </button>
      </div>

      {loading && siemIntegrations.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-blue-500" />
        </div>
      ) : siemIntegrations.length === 0 ? (
        <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <FontAwesomeIcon icon={faPlug} className="text-4xl text-gray-400 mb-4" />
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            No SIEM integrations configured
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Add an integration to start sending events to your SIEM
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {siemIntegrations.map((integration) => (
            <div
              key={integration._id}
              className={`p-4 rounded-lg border ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getHealthIcon(integration.health?.status)}
                  <div>
                    <h3 className="font-medium">{integration.name}</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {siemTypes.find(t => t.id === integration.type)?.name || integration.type}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {integration.stats?.totalEventsSent || 0} events sent
                  </span>
                  <button
                    onClick={() => handleToggleSiem(integration._id)}
                    className={`p-2 rounded ${integration.isEnabled ? 'text-green-500' : 'text-gray-400'}`}
                    title={integration.isEnabled ? 'Disable' : 'Enable'}
                  >
                    <FontAwesomeIcon icon={integration.isEnabled ? faToggleOn : faToggleOff} className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleTestSiem(integration._id)}
                    className={`p-2 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    title="Test connection"
                  >
                    <FontAwesomeIcon icon={faSync} />
                  </button>
                  <button
                    onClick={() => {
                      setEditingSiem(integration);
                      setSiemForm({
                        name: integration.name,
                        type: integration.type,
                        config: integration.config || {},
                        eventTypes: integration.eventTypes || {}
                      });
                      setShowSiemModal(true);
                    }}
                    className={`p-2 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDeleteSiem(integration._id)}
                    className="p-2 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              {integration.health?.lastError && (
                <p className="mt-2 text-sm text-red-500">
                  Last error: {integration.health.lastError}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderNotificationPreferences = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Notification Preferences</h2>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Configure which notifications you want to receive
        </p>
      </div>

      {notifPreferences ? (
        <div className="space-y-6">
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className="font-medium mb-4">In-App Notifications</h3>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(notifPreferences.inApp || {}).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) =>
                      setNotifPreferences(prev => ({
                        ...prev,
                        inApp: { ...prev.inApp, [key]: e.target.checked }
                      }))
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">
                    {notificationService.getTypeInfo(key).label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className="font-medium mb-4">Email Notifications</h3>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(notifPreferences.email || {}).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) =>
                      setNotifPreferences(prev => ({
                        ...prev,
                        email: { ...prev.email, [key]: e.target.checked }
                      }))
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">
                    {notificationService.getTypeInfo(key).label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={handleSaveNotifPreferences}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            Save Preferences
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-blue-500" />
        </div>
      )}
    </div>
  );

  const renderRemediationDatabase = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Remediation Database</h2>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Vendor-specific remediation guidance for MITRE ATT&CK techniques
          </p>
        </div>
        <button
          onClick={handleSeedRemediation}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              <FontAwesomeIcon icon={faDatabase} className="mr-2" />
              Seed Sample Data
            </>
          )}
        </button>
      </div>

      {remediationStats ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-blue-500">
              {remediationStats.totalRemediations || 0}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Remediations
            </div>
          </div>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-green-500">
              {remediationStats.totalTechniques || 0}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Techniques Covered
            </div>
          </div>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-purple-500">
              {remediationStats.byVendor?.length || 0}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Vendors Supported
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-blue-500" />
        </div>
      )}

      {remediationStats?.byVendor?.length > 0 && (
        <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} p-4`}>
          <h3 className="font-medium mb-4">Coverage by Vendor</h3>
          <div className="space-y-2">
            {remediationStats.byVendor.map((v) => {
              const vendorInfo = remediationService.getVendorInfo(v.vendor);
              return (
                <div key={v.vendor} className="flex items-center justify-between">
                  <span>{vendorInfo.name}</span>
                  <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {v.count} remediations ({v.uniqueTechniques} techniques)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const handleSaveBranding = async () => {
    try {
      setLoading(true);
      await settingsService.updateSettings({ branding: brandingForm });
      toast.success('Branding settings saved');
      fetchAppSettings();
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e, type = 'logo') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, SVG, and WebP are allowed.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      setLoading(true);
      await settingsService.uploadLogo(file, type);
      toast.success('Logo uploaded successfully');
      fetchAppSettings();
    } catch (error) {
      console.error('Error uploading logo:', error);
      const message = error.response?.data?.message || 'Failed to upload logo';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLogo = async (type = 'logo') => {
    try {
      setLoading(true);
      await settingsService.removeLogo(type);
      toast.success('Logo removed');
      fetchAppSettings();
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLocalization = async () => {
    try {
      setLoading(true);
      await settingsService.updateSettings({ localization: localizationForm });
      toast.success('Localization settings saved');
      fetchAppSettings();
    } catch (error) {
      console.error('Error saving localization:', error);
      toast.error('Failed to save localization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      setLoading(true);
      // Validate required fields
      if (!generalForm.smtpHost || !generalForm.smtpPort || !generalForm.smtpUser) {
        toast.error('Please fill in all required SMTP fields');
        return;
      }

      // Prepare SMTP config (exclude password if empty)
      const smtpConfig = {
        smtpHost: generalForm.smtpHost,
        smtpPort: parseInt(generalForm.smtpPort),
        smtpUser: generalForm.smtpUser,
        smtpSecure: generalForm.smtpSecure,
        fromEmail: generalForm.fromEmail || generalForm.smtpUser,
        fromName: generalForm.fromName || 'PurpleHQ'
      };

      // Only include password if it was changed (not empty)
      if (generalForm.smtpPassword) {
        smtpConfig.smtpPassword = generalForm.smtpPassword;
      }

      await settingsService.updateSettings({ smtp: smtpConfig });
      toast.success('SMTP settings saved successfully');

      // Clear password field after save
      setGeneralForm(prev => ({ ...prev, smtpPassword: '' }));
      fetchAppSettings();
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setLoading(true);
      await settingsService.testEmail();
      toast.success('Test email sent successfully! Check your inbox.');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.error || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const renderBrandingSettings = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold">{t('settings.branding')}</h2>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Customize the appearance of your application
        </p>
      </div>

      <div className="space-y-6">
        {/* Logo Upload - Dark Mode (Light colored logo for dark backgrounds) */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h3 className="font-medium mb-4">Logo (Dark Background)</h3>
          <div className="flex items-center space-x-6">
            <div className={`w-32 h-32 rounded-lg border-2 border-dashed flex items-center justify-center ${
              darkMode ? 'border-gray-600 bg-gray-900' : 'border-gray-300 bg-gray-800'
            }`}>
              {appSettings?.branding?.logo ? (
                <img
                  src={appSettings.branding.logo}
                  alt="Logo (Dark)"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <FontAwesomeIcon icon={faImage} className="text-4xl text-gray-400" />
              )}
            </div>
            <div className="space-y-2">
              <input
                type="file"
                ref={logoInputRef}
                onChange={(e) => handleLogoUpload(e, 'logo')}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => logoInputRef.current?.click()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Upload Logo
              </button>
              {appSettings?.branding?.logo && (
                <button
                  onClick={() => handleRemoveLogo('logo')}
                  className="px-4 py-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg ml-2"
                >
                  Remove
                </button>
              )}
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Light-colored logo for dark backgrounds (PNG/SVG, max 5MB)
              </p>
            </div>
          </div>
        </div>

        {/* Logo Upload - Light Mode (Dark colored logo for light backgrounds) */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h3 className="font-medium mb-4">Logo (Light Background)</h3>
          <div className="flex items-center space-x-6">
            <div className={`w-32 h-32 rounded-lg border-2 border-dashed flex items-center justify-center ${
              darkMode ? 'border-gray-600 bg-gray-100' : 'border-gray-300 bg-white'
            }`}>
              {appSettings?.branding?.logoLight ? (
                <img
                  src={appSettings.branding.logoLight}
                  alt="Logo (Light)"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <FontAwesomeIcon icon={faImage} className="text-4xl text-gray-400" />
              )}
            </div>
            <div className="space-y-2">
              <input
                type="file"
                ref={logoLightInputRef}
                onChange={(e) => handleLogoUpload(e, 'logoLight')}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => logoLightInputRef.current?.click()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Upload Logo
              </button>
              {appSettings?.branding?.logoLight && (
                <button
                  onClick={() => handleRemoveLogo('logoLight')}
                  className="px-4 py-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg ml-2"
                >
                  Remove
                </button>
              )}
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Dark-colored logo for light backgrounds (PNG/SVG, max 5MB)
              </p>
            </div>
          </div>
        </div>

        {/* App Name & Tagline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.appName')}</label>
            <input
              type="text"
              value={brandingForm.appName}
              onChange={(e) => setBrandingForm(prev => ({ ...prev, appName: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              }`}
              placeholder="Purple Team Operations"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.tagline')}</label>
            <input
              type="text"
              value={brandingForm.tagline}
              onChange={(e) => setBrandingForm(prev => ({ ...prev, tagline: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              }`}
              placeholder="Red Team & Blue Team Collaboration Platform"
            />
          </div>
        </div>

        {/* Colors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.primaryColor')}</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={brandingForm.primaryColor}
                onChange={(e) => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={brandingForm.primaryColor}
                onChange={(e) => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                className={`flex-1 p-2 rounded border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.secondaryColor')}</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={brandingForm.secondaryColor}
                onChange={(e) => setBrandingForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={brandingForm.secondaryColor}
                onChange={(e) => setBrandingForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                className={`flex-1 p-2 rounded border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSaveBranding}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (
            <>
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {t('common.save')}
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderLocalizationSettings = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold">{t('settings.localization')}</h2>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Configure language and regional settings
        </p>
      </div>

      <div className="space-y-6">
        {/* Your Personal Language */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h3 className="font-medium mb-1">{t('profile.languagePreference')}</h3>
          <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('profile.languageDescription')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(languages).filter(([code]) => availableLanguages.includes(code)).map(([code, lang]) => (
              <button
                key={code}
                onClick={() => setLanguage(code)}
                className={`p-3 rounded-lg text-left ${
                  language === code
                    ? 'bg-blue-500 text-white'
                    : darkMode
                    ? 'bg-gray-800 hover:bg-gray-600'
                    : 'bg-white hover:bg-gray-100 border'
                }`}
              >
                <div className="font-medium">{lang.nativeName}</div>
                <div className={`text-sm ${language === code ? 'text-blue-100' : 'text-gray-500'}`}>
                  {lang.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* System Default Language (Admin) */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <label className="block text-sm font-medium mb-1">{t('settings.defaultLanguage')}</label>
          <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Default language for new users who haven't set a preference
          </p>
          <select
            value={localizationForm.defaultLanguage}
            onChange={(e) => setLocalizationForm(prev => ({ ...prev, defaultLanguage: e.target.value }))}
            className={`w-full md:w-64 p-2 rounded border ${
              darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
            }`}
          >
            {Object.entries(languages).map(([code, lang]) => (
              <option key={code} value={code}>{lang.nativeName} ({lang.name})</option>
            ))}
          </select>
        </div>

        {/* Date & Time Format */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.dateFormat')}</label>
            <select
              value={localizationForm.dateFormat}
              onChange={(e) => setLocalizationForm(prev => ({ ...prev, dateFormat: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              }`}
            >
              {settingsService.dateFormats.map(fmt => (
                <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.timeFormat')}</label>
            <select
              value={localizationForm.timeFormat}
              onChange={(e) => setLocalizationForm(prev => ({ ...prev, timeFormat: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              }`}
            >
              {settingsService.timeFormats.map(fmt => (
                <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.timezone')}</label>
          <select
            value={localizationForm.timezone}
            onChange={(e) => setLocalizationForm(prev => ({ ...prev, timezone: e.target.value }))}
            className={`w-full md:w-64 p-2 rounded border ${
              darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
            }`}
          >
            {settingsService.timezones.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <button
          onClick={handleSaveLocalization}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (
            <>
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {t('common.save')}
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderBackupSettings = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold">{t('settings.backup') || 'Backup & Restore'}</h2>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Create and manage database backups, or restore from a previous backup
        </p>
      </div>

      {/* Progress indicator */}
      {backupProgress && (
        <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border ${darkMode ? 'border-blue-800' : 'border-blue-200'}`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-3 text-blue-500" />
            <span>{backupProgress}</span>
          </div>
        </div>
      )}

      {/* Create Backup Section */}
      <div className={`p-4 rounded-lg mb-6 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <h3 className="font-medium mb-3 flex items-center">
          <FontAwesomeIcon icon={faServer} className="mr-2 text-green-500" />
          Create Backup
        </h3>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Create a complete backup of your database including all projects, actions, findings, users, and settings.
        </p>
        <button
          onClick={handleCreateBackup}
          disabled={backupLoading}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
        >
          {backupLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Create Backup Now
            </>
          )}
        </button>
      </div>

      {/* Restore Section */}
      <div className={`p-4 rounded-lg mb-6 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <h3 className="font-medium mb-3 flex items-center">
          <FontAwesomeIcon icon={faUpload} className="mr-2 text-orange-500" />
          Restore from Backup
        </h3>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Upload a backup file to restore your database. Warning: This will overwrite all current data!
        </p>
        <input
          type="file"
          ref={restoreInputRef}
          onChange={handleRestoreBackup}
          accept=".gz,.tar,.tar.gz,.zip"
          className="hidden"
        />
        <button
          onClick={() => restoreInputRef.current?.click()}
          disabled={backupLoading}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faUpload} className="mr-2" />
          Upload & Restore
        </button>
      </div>

      {/* Existing Backups */}
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <h3 className="font-medium mb-3 flex items-center">
          <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-500" />
          Available Backups
        </h3>

        {backupLoading && !backupProgress ? (
          <div className="flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-500" />
          </div>
        ) : backupList.length === 0 ? (
          <p className={`text-sm py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No backups found. Create your first backup above.
          </p>
        ) : (
          <div className="space-y-2">
            {backupList.map((backup, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                } border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faDatabase} className={`mr-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">{backup.filename}</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(backup.createdAt).toLocaleString()} • {formatFileSize(backup.size)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownloadBackup(backup.filename)}
                    className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                    title="Download"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                  <button
                    onClick={() => handleDeleteBackup(backup.filename)}
                    className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'} border ${darkMode ? 'border-yellow-800' : 'border-yellow-200'}`}>
        <p className={`text-sm ${darkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
          <strong>Note:</strong> Backups include all database collections. For best practices,
          create regular backups and store them securely off-server. Restore operations will
          temporarily make the application unavailable.
        </p>
      </div>
    </div>
  );

  const renderGeneralSettings = () => (
    <div>
      <h2 className="text-xl font-semibold mb-6">{t('settings.general')}</h2>

      {/* SMTP Email Configuration */}
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow mb-6`}>
        <h3 className="text-lg font-semibold mb-4">
          <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-blue-500" />
          Email / SMTP Configuration
        </h3>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Configure SMTP settings for sending email notifications, password resets, and alerts.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              SMTP Host *
            </label>
            <input
              type="text"
              value={generalForm.smtpHost || ''}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, smtpHost: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              placeholder="smtp.gmail.com"
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              SMTP Port *
            </label>
            <input
              type="number"
              value={generalForm.smtpPort || ''}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, smtpPort: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              placeholder="587"
            />
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Use 587 for TLS, 465 for SSL
            </p>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              SMTP Username *
            </label>
            <input
              type="text"
              value={generalForm.smtpUser || ''}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, smtpUser: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              placeholder="your-email@gmail.com"
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              SMTP Password *
            </label>
            <input
              type="password"
              value={generalForm.smtpPassword || ''}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, smtpPassword: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              placeholder="••••••••••••"
            />
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Leave blank to keep existing password
            </p>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              From Email
            </label>
            <input
              type="email"
              value={generalForm.fromEmail || ''}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, fromEmail: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              placeholder="noreply@purplehq.ch"
            />
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Defaults to SMTP username
            </p>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              From Name
            </label>
            <input
              type="text"
              value={generalForm.fromName || ''}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, fromName: e.target.value }))}
              className={`w-full p-2 rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              placeholder="PurpleHQ"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={generalForm.smtpSecure || false}
              onChange={(e) => setGeneralForm(prev => ({ ...prev, smtpSecure: e.target.checked }))}
              className="rounded"
            />
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Use SSL/TLS (for port 465)
            </span>
          </label>
        </div>

        <div className="mt-6 flex items-center space-x-3">
          <button
            onClick={handleSaveGeneral}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                Save SMTP Settings
              </>
            )}
          </button>
          <button
            onClick={handleTestEmail}
            disabled={loading}
            className={`px-4 py-2 rounded-lg ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            } disabled:opacity-50`}
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
            Test Email
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <PageHeader
          title={t('settings.title') || 'Settings'}
          icon={<FontAwesomeIcon icon={faCog} />}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className={`w-full md:w-64 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-4`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg mb-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white'
                    : darkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                <span className="flex items-center">
                  <FontAwesomeIcon icon={tab.icon} className="mr-3" />
                  {tab.label}
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="text-sm" />
              </button>
            ))}
          </div>

          {/* Content */}
          <div className={`flex-1 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            {activeTab === 'branding' && renderBrandingSettings()}
            {activeTab === 'localization' && renderLocalizationSettings()}
            {activeTab === 'apikeys' && renderIntegrationKeys()}
            {activeTab === 'integrations' && renderSiemIntegrations()}
            {activeTab === 'notifications' && renderNotificationPreferences()}
            {activeTab === 'remediation' && renderRemediationDatabase()}
            {activeTab === 'backup' && renderBackupSettings()}
            {activeTab === 'general' && renderGeneralSettings()}
          </div>
        </div>
      </div>

      {/* SIEM Modal */}
      {showSiemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`w-full max-w-lg rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <h2 className="text-xl font-semibold mb-4">
              {editingSiem ? 'Edit Integration' : 'Add SIEM Integration'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={siemForm.name}
                  onChange={(e) => setSiemForm(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full p-2 rounded border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                  placeholder="My SIEM Integration"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={siemForm.type}
                  onChange={(e) => setSiemForm(prev => ({ ...prev, type: e.target.value, config: {} }))}
                  className={`w-full p-2 rounded border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                >
                  {siemTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Dynamic config fields based on type */}
              {siemService.getTypeConfig(siemForm.type)?.fields?.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      value={field.key.split('.').reduce((o, k) => o?.[k], siemForm.config) || field.default || ''}
                      onChange={(e) => {
                        const keys = field.key.split('.');
                        setSiemForm(prev => {
                          const newConfig = { ...prev.config };
                          let obj = newConfig;
                          for (let i = 0; i < keys.length - 1; i++) {
                            obj[keys[i]] = obj[keys[i]] || {};
                            obj = obj[keys[i]];
                          }
                          obj[keys[keys.length - 1]] = e.target.value;
                          return { ...prev, config: newConfig };
                        });
                      }}
                      className={`w-full p-2 rounded border ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    >
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type === 'password' ? 'password' : field.type}
                      value={field.key.split('.').reduce((o, k) => o?.[k], siemForm.config) || ''}
                      onChange={(e) => {
                        const keys = field.key.split('.');
                        setSiemForm(prev => {
                          const newConfig = { ...prev.config };
                          let obj = newConfig;
                          for (let i = 0; i < keys.length - 1; i++) {
                            obj[keys[i]] = obj[keys[i]] || {};
                            obj = obj[keys[i]];
                          }
                          obj[keys[keys.length - 1]] = e.target.value;
                          return { ...prev, config: newConfig };
                        });
                      }}
                      className={`w-full p-2 rounded border ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                      placeholder={field.placeholder || field.default || ''}
                    />
                  )}
                </div>
              ))}

              {/* Query Configuration */}
              <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <label className="flex items-center space-x-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={siemForm.queryConfig?.queryEnabled || false}
                    onChange={(e) =>
                      setSiemForm(prev => ({
                        ...prev,
                        queryConfig: { ...prev.queryConfig, queryEnabled: e.target.checked }
                      }))
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Enable SIEM Querying (BAS Correlation)</span>
                </label>
                <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  When enabled, allows querying this SIEM to check if red team actions were detected.
                </p>
                {siemForm.queryConfig?.queryEnabled && siemService.getTypeConfig(siemForm.type)?.queryFields?.map((field) => (
                  <div key={field.key} className="mt-2">
                    <label className="block text-xs font-medium mb-1">{field.label}</label>
                    <input
                      type={field.type === 'password' ? 'password' : field.type}
                      value={field.key.split('.').reduce((o, k) => o?.[k], siemForm) || ''}
                      onChange={(e) => {
                        const keys = field.key.split('.');
                        setSiemForm(prev => {
                          const updated = { ...prev };
                          let obj = updated;
                          for (let i = 0; i < keys.length - 1; i++) {
                            obj[keys[i]] = { ...(obj[keys[i]] || {}) };
                            obj = obj[keys[i]];
                          }
                          obj[keys[keys.length - 1]] = e.target.value;
                          return updated;
                        });
                      }}
                      className={`w-full p-2 rounded border text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                      placeholder={field.placeholder || field.default || ''}
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Event Types to Send</label>
                <div className="space-y-2">
                  {['actions', 'findings', 'agentEvents', 'playbookExecutions'].map((type) => (
                    <label key={type} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={siemForm.eventTypes?.[type] || false}
                        onChange={(e) =>
                          setSiemForm(prev => ({
                            ...prev,
                            eventTypes: { ...prev.eventTypes, [type]: e.target.checked }
                          }))
                        }
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm capitalize">{type.replace(/([A-Z])/g, ' $1')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowSiemModal(false);
                  setEditingSiem(null);
                }}
                className={`px-4 py-2 rounded ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSiem}
                disabled={loading || !siemForm.name}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
